<template>
  <div class="sensors">
    <Loading v-if="loading || !chartLoaded" />
    <div class="sensors__header">
      <div class="container">
        <Dropdown
          v-if="selectedSensors.options?.length"
          class="sensors__header-dropdown"
          :selected-option="selectedSensors.selectedOption[0]"
          :options="selectedSensors.options"
          data-testid="selectIndividualSensors"
          @select-option="selectSensors"
        />
        <Button
            v-if="selectedSensors.selectedOption?.length"
            class="checkAllTemperatures-button"
            variant="filled"
            :title="$t('checkThisTemperature') || 'Check this'"
            @click="handleCheckRequest"
        />
      </div>
      <div class="chooseDate">
        <div class="date-block">
          <div class="date-inputs">
            <Button
                class="week-nav-button"
                variant="bordered"
                :title="$t('prevWeek') || 'Prev week'"
                @click="prevWeek"
            />
            <div class="input">
              <Calendar
                type="date"
                format="DD/MM/YYYY"
                :value="dateRange.startTime"
                name="startTime"
                @change="changeDate"
                @close-panel="closeTimePanel"
              />
            </div>
            <img
              class="date-img"
              src="@/assets/icons/Hyphen.svg"
              alt="Hyphen"
            />
            <div class="input">
              <Calendar
                type="date"
                format="DD/MM/YYYY"
                :value="dateRange.endTime"
                name="endTime"
                @change="changeDate"
                @close-panel="closeTimePanel"
              />
            </div>
            <Button
                class="update-date-button"
                variant="filled"
                style="margin-left: 12px"
                :title="$t('update')"
                :disabled="!isDateCorrect || !isSixMonthValid"
                @click="handleUpdateClick"
            />
            <Button
                v-if="canGoNextWeek"
                class="week-nav-button"
                variant="bordered"
                style="margin-left: 12px"
                :title="$t('nextWeek') || 'Next week'"
                @click="nextWeek"
            />
          </div>
          <p v-if="!isSixMonthValid" class="date-error">
            {{ $t('errorSixMonths') || 'Range cannot exceed 6 months' }}
          </p>
          <p v-if="!isDateCorrect" class="date-error">
            {{ $t('errorDate') }}
          </p>
        </div>
      </div>
    </div>
    <div v-if="!loading && !selectedSensors.options?.length" class="sensors__noData">
      <p>{{ $t('noData') }}</p>
    </div>
    <SensorsMain
        v-if="selectedSensorsData?.length && pharmacy"
        :pharmacy="pharmacy"
        :line-chart-key="lineChartKey"
        :data-line-chart="dataLineChart"
        :alarms-sensor="alarmsSensor"
        :is-date-correct="isDateCorrect"
        :selected-sensors-data="selectedSensorsData"
        :selected-option="selectedSensors.selectedOption"
        :temperature-sensor-stamp="temperatureSensorStamp"
        :date-range="dateRange"
        :all-sensor-stamps="allSensorStamps"
        @refresh-alarms="initAlarmsSensor"
    />
  </div>
</template>

<script lang="ts">
import { Vue, Options } from 'vue-class-component';
import { mapActions, mapState } from 'vuex';

import Loading from '@/views/Loading.vue';
import SensorsMain from '../SensorsMain/SensorsMain.vue';

import { format } from 'date-fns';

import {
  DataCreateNewSensor,
  DataSensor,
  Sensor,
  TemperatureSensorStamp
} from '@/store/modules/pharmacy/types';
import { InputFileEvent, Option } from '@/types';
import {
  prevWeekWindow,
  nextWeekWindow,
  canShiftWeekForward
} from '@/utils/shiftDateRange';

const transformationDate = new Date().setDate(new Date().getDate() - 3);

@Options({
  name: 'IndividualSensors',
  components: {
    Loading,
    SensorsMain
  },
  data() {
    return {
      selectedSensors: {
        selectedOption: [],
        options: []
      },
      dateRange: {
        startTime: format(new Date(transformationDate), 'dd/MM/yyyy'),
        endTime: format(new Date(), 'dd/MM/yyyy')
      },
      alarmsSensor: [],
      lineChartKey: 0,
      activeSensor: null,
      dataLineChart: [],
      selectedSensorsData: [],
      loading: false,
      chartLoaded: false,
      showTimePanel: false,
      totalEndTime: [],
      allSensorStamps: [] as Array<{
        sensor: Sensor;
        stamp: TemperatureSensorStamp | null;
      }>
    };
  },
  created() {
    if (this.user) {
      this.show();
    }
  },
  computed: {
    ...mapState('account', ['user']),
    ...mapState('pharmacy', ['pharmacy', 'sensor', 'temperatureSensorStamp']),
    isDateCorrect() {
      if (this.isDatePresent) {
        const startTime = this.dateRange.startTime.split('/');
        const endTime = this.dateRange.endTime.split('/');

        const defaultParams = {
          startTime: new Date(
            +startTime[2],
            startTime[1] - 1,
            +startTime[0]
          ).valueOf(),
          endTime: new Date(+endTime[2], endTime[1] - 1, +endTime[0]).valueOf()
        };

        return defaultParams.endTime >= defaultParams.startTime;
      }

      return true;
    },
    isDatePresent() {
      const startTime = this.dateRange.startTime.split('/');
      const endTime = this.dateRange.endTime.split('/');

      return (
        new Date(+startTime[2], startTime[1] - 1, +startTime[0]).valueOf() &&
        new Date(+endTime[2], endTime[1] - 1, +endTime[0]).valueOf()
      );
    },
    isSixMonthValid() {
      const startParts = this.dateRange.startTime.split('/');
      const endParts = this.dateRange.endTime.split('/');

      const start = new Date(+startParts[2], startParts[1] - 1, +startParts[0]);
      const end = new Date(+endParts[2], endParts[1] - 1, +endParts[0]);

      const diffInMs = end.getTime() - start.getTime();
      const sixMonthsInMs = 1000 * 60 * 60 * 24 * 183;

      return diffInMs <= sixMonthsInMs;
    },
    canGoNextWeek() {
      return canShiftWeekForward(this.dateRange);
    }
  },
  methods: {
    ...mapActions('pharmacy', [
      'getPharmacy',
      'getSensor',
      'getTemperatureSensorStamp',
      'sendTemperatureSensorsStamp',
      'getAlarmsSensor'
    ]),
    async show() {
      await this.loadSensors();
      this.initSensorsOptions();
    },
    async loadSensors() {
      this.loading = true;
      await this.getPharmacy(this.user.pharmacyId);
      this.loading = false;
    },
    initSensorsOptions() {
      if (this.pharmacy) {
        const sensorsOptionsData = this.pharmacy?.sensors.map(
          (i: DataCreateNewSensor) => ({
            value: i.id,
            label: i.name
          })
        );

        this.selectedSensors.options = sensorsOptionsData;

        if (
          sensorsOptionsData.length &&
          (!this.selectedSensors.selectedOption ||
            this.selectedSensors.selectedOption.length === 0)
        ) {
          this.selectedSensors.selectedOption = [sensorsOptionsData[0]];
          this.activeSensor = this.pharmacy.sensors[0];
        } else {
          this.chartLoaded = true;
        }
      }
    },
    async initAlarmsSensor() {
      this.loading = true;

      const selectedIds = this.selectedSensors.selectedOption?.map(
        (i: Option) => i.value
      );

      const matchedSensors = this.pharmacy?.sensors.filter((sensor: Sensor) =>
        selectedIds.includes(sensor.id)
      );

      const sensorsSn = matchedSensors.map((i: Sensor) => i.sn);

      const startTime = this.dateRange.startTime.split('/');
      this.getTotalEndTime();

      const defaultParams = {
        startTime: new Date(
          +startTime[2],
          startTime[1] - 1,
          +startTime[0]
        ).valueOf(),
        endTime: new Date(
          +this.totalEndTime[2],
          +this.totalEndTime[1] - 1,
          +this.totalEndTime[0],
          +this.totalEndTime[3],
          +this.totalEndTime[4],
          +this.totalEndTime[5]
        ).valueOf()
      };

      if (
        sensorsSn.length &&
        defaultParams.startTime &&
        defaultParams.endTime &&
        this.isDateCorrect
      ) {
        const snToIdMap = Object.fromEntries(
          matchedSensors.map((sensor: Sensor) => [sensor.sn, sensor.id])
        );

        const responses = await Promise.all(
          sensorsSn.map((sensorSn: string) => {
            const id = snToIdMap[sensorSn];
            return this.getAlarmsSensor({ ...defaultParams, sensorSn, id });
          })
        );

        this.alarmsSensor = responses.map((sensor: DataSensor) =>
          JSON.parse(JSON.stringify(sensor))
        );
      }

      this.loading = false;
    },
    async handleCheckRequest() {
      const selectedIds = this.selectedSensors.selectedOption.map(
        (opt: Option) => opt.value
      );
      const sensorSns = this.pharmacy.sensors
        .filter((s: Sensor) => selectedIds.includes(s.id))
        .map((s: Sensor) => s.sn);

      await this.processCheckRequests(sensorSns);
    },
    getRandomColorFromScale() {
      const colorScale = [
        '#E6194B',
        '#3CB44B',
        '#FFE119',
        '#4363D8',
        '#F58231',
        '#911EB4',
        '#42D4F4',
        '#F032E6',
        '#BFEF45',
        '#FABEBE',
        '#469990',
        '#E6BEFF',
        '#9A6324',
        '#FFFAC8',
        '#800000',
        '#AAFFC3',
        '#808000',
        '#FFD8B1',
        '#000075',
        '#A9A9A9'
      ];

      const index = Math.floor(Math.random() * colorScale.length);
      return colorScale[index];
    },
    async initTemperatureSensorStamp(sensorId: string) {
      this.loading = true;
      await this.getTemperatureSensorStamp({ boxId: sensorId, last: true });
      this.loading = false;
    },
    async loadSensor() {
      this.chartLoaded = false;
      this.loading = true;

      const sensorIds = this.selectedSensors.selectedOption?.map(
        (i: Option) => i.value
      );

      const startTime = this.dateRange.startTime.split('/');
      this.getTotalEndTime();

      const defaultParams = {
        startTime: new Date(
          +startTime[2],
          startTime[1] - 1,
          +startTime[0]
        ).valueOf(),
        endTime: new Date(
          +this.totalEndTime[2],
          +this.totalEndTime[1] - 1,
          +this.totalEndTime[0],
          +this.totalEndTime[3],
          +this.totalEndTime[4],
          +this.totalEndTime[5]
        ).valueOf(),
        pharmacyId: this.user.pharmacyId
      };

      if (sensorIds?.length && this.isDateCorrect) {
        const responses = await Promise.all(
          sensorIds.map((sensorId: string) =>
            this.getSensor({ ...defaultParams, sensorId })
          )
        );

        this.dataLineChart = responses.map(
          (sensor: DataSensor, index: number) => {
            const cloned = JSON.parse(JSON.stringify(sensor));

            const matchedSensor = this.pharmacy?.sensors?.find(
              (s: Sensor) => s.id === sensorIds[index]
            );
            const color =
              matchedSensor?.color || this.getRandomColorFromScale();

            this.initTemperatureSensorStamp(cloned.datasets[0].value);

            cloned.id = cloned.datasets[0].value;

            // Color the temperature line(s) and legend, skip the MAX/MIN
            // threshold (dashed) datasets so they keep their own styling.
            cloned.datasets.forEach((ds: any) => {
              const label = (ds.label || '').toUpperCase();
              if (!label.includes('MAX') && !label.includes('MIN')) {
                ds.backgroundColor = color;
                ds.borderColor = color;
                ds.pointBackgroundColor = color;
                ds.pointBorderColor = color;
                // Thresholds used to paint out-of-range points/segments red.
                ds.sensorMin = matchedSensor?.min;
                ds.sensorMax = matchedSensor?.max;
              }
            });

            return cloned;
          }
        );

        this.lineChartKey++;
      }

      this.chartLoaded = true;
      this.loading = false;
    },
    getTotalEndTime() {
      const arrayEndTime = this.dateRange.endTime.split('/');
      const arrayToday = format(new Date(), 'dd/MM/yyyy/HH/mm/ss').split('/');
      // A past end date covers its whole day; only today stops at "now".
      const isToday =
        arrayEndTime.join('/') === arrayToday.slice(0, 3).join('/');
      const arrayHour = isToday ? arrayToday.slice(-3) : ['23', '59', '59'];

      this.totalEndTime = arrayEndTime.concat(arrayHour);
    },
    changeDate(e: InputFileEvent) {
      const { name, value } = e.target;

      this.dateRange = {
        ...this.dateRange,
        [name]: value
      };
    },
    selectSensors(option: Option) {
      if (!option) return;

      this.selectedSensors.selectedOption = [option];

      this.activeSensor =
        this.pharmacy?.sensors?.find(
          (i: DataCreateNewSensor) => i.id === option.value
        ) || null;
    },
    closeTimePanel() {
      this.showTimePanel = false;
    },
    async handleUpdateClick() {
      if (this.isDateCorrect && this.isSixMonthValid) {
        this.loading = true;
        await Promise.all([this.loadSensor(), this.initAlarmsSensor()]);
        this.loading = false;
      }
    },
    async prevWeek() {
      this.dateRange = prevWeekWindow(this.dateRange);
      await this.handleUpdateClick();
    },
    async nextWeek() {
      if (!this.canGoNextWeek) return;
      this.dateRange = nextWeekWindow(this.dateRange);
      await this.handleUpdateClick();
    },
    async processCheckRequests(sns: string[]) {
      if (!sns.length) return;

      this.loading = true;
      try {
        for (const sn of sns) {
          await this.sendTemperatureSensorsStamp({ boxId: sn });
        }
        // Re-fetch the check stamp for the selected sensor so the
        // "last checked by" text updates immediately, without a page reload.
        await this.initTemperatureSensorStamp(sns[0]);
      } catch (error) {
        console.error('Check failed', error);
      } finally {
        this.loading = false;
      }
    }
  },
  watch: {
    user() {
      if (this.user) {
        this.show();
      }
    },
    'selectedSensors.selectedOption': {
      immediate: true,
      handler(options: Option[]) {
        if (!Array.isArray(options) || !options.length) return;

        const selectedIds = options.map((el: Option) => el.value);

        if (this.pharmacy) {
          this.selectedSensorsData = this.pharmacy.sensors.filter(
            (sensor: Sensor) => selectedIds.includes(sensor.id)
          );
          this.allSensorStamps = [];

          this.initTemperatureSensorStamp(selectedIds[0]);

          this.loadSensor();
          this.initAlarmsSensor();
        }
      }
    }
  }
})
export default class IndividualSensors extends Vue {}
</script>

<style lang="scss" scoped>
.sensors {
  display: flex;
  flex-direction: column;
  padding: 0 24px 24px;
  .sensors__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    column-gap: 15px;
    padding: 24px 0;
    .container {
      display: flex;
      align-items: center;
      .sensors__header-dropdown {
        height: 40px;
        width: 250px !important;
      }
      .checkAllTemperatures-button {
        margin: 0 12px;
      }
    }
    .chooseDate {
      display: flex;
      height: 42px;
      .date-block {
        width: 100%;
        height: 100%;
        margin-left: 10px;
        .date-inputs {
          display: flex;
          align-items: center;
          .week-nav-button {
            white-space: nowrap;
          }
          .week-nav-button:first-child {
            margin-right: 12px;
          }
          .formkit-outer {
            height: 42px;
            margin-bottom: 0 !important;
            .formkit-wrapper {
              height: 42px;
              .formkit-inner {
                height: 42px;
              }
            }
          }
          .input {
            position: relative;
            z-index: 2;
            margin-bottom: 0 !important;
            .input-img {
              position: absolute;
              top: 12px;
              right: 12px;
              z-index: 0;
            }
          }
          .date-img {
            margin: 0 8px;
          }
        }
        .date-error {
          color: $red;
          font-size: $fourteenSize;
          margin-top: 10px;
        }
      }
    }
  }
  .sensors__noData {
    margin-top: 24px;
    p {
      color: $mid-grey;
      font-size: $fourteenSize;
    }
  }
}
</style>
